import { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import {
  DateFilterOperator,
  KnownDateFilterTimeFrame,
  RelativeTimeUnit,
} from "@redotech/redo-views-common/advanced-filters/date-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { SortDirection, TableSort } from "@redotech/redo-views-common/table";
import { DEFAULT_PAGE_SIZE, viewTypes } from "./views";

export enum OrderTrackingShipmentsSystemView {
  ALL = "all",
  LABEL_CREATED = "label-created",
  SEVEN_DAYS_IN_TRANSIT = "seven-days-in-transit",
  IN_TRANSIT = "in-transit",
  OUT_FOR_DELIVERY = "out-for-delivery",
  DELIVERED = "delivered",
  EXCEPTIONS = "exceptions",
  PROBLEM_ORDERS = "problem-orders",
}

export const orderTrackingShipmentSortDefault: TableSort = {
  direction: SortDirection.DESC,
  key: "firstInTransitAt",
};

const getDeliveryStatusFilter = (statuses: string[]) =>
  ({
    name: "deliveryStatus",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: statuses,
    operator: ArrayStringFilterOperator.ANY_OF,
  }) as AdvancedFilterData;

// "More than N days ago": first_in_transit_at < now - N days.
const getFirstInTransitBeforeRelativeDaysFilter = (days: number) =>
  ({
    name: "firstInTransitAt",
    type: AdvancedFilterType.DATE,
    value: KnownDateFilterTimeFrame.RELATIVE,
    operator: DateFilterOperator.BEFORE,
    relativeTime: { value: days, unit: RelativeTimeUnit.DAYS },
  }) as AdvancedFilterData;

const defaultColumns = [
  { key: "orderName", width: 150, hidden: false },
  { key: "customerName", width: 150, hidden: false },
  { key: "customerEmail", width: 180, hidden: false },
  { key: "orderDate", width: 130, hidden: false },
  { key: "status", width: 120, hidden: false },
  { key: "trackingNumber", width: 160, hidden: false },
  { key: "carrier", width: 120, hidden: false },
  { key: "deliveryStatus", width: 140, hidden: false },
  { key: "firstInTransitAt", width: 140, hidden: false },
  { key: "estimatedDeliveryAt", width: 140, hidden: false },
  { key: "outForDeliveryAt", width: 140, hidden: true },
  { key: "deliveredAt", width: 130, hidden: false },
];

export const StaticSystemViews: Record<
  OrderTrackingShipmentsSystemView,
  viewTypes["order-tracking-shipments"]
> = {
  [OrderTrackingShipmentsSystemView.ALL]: {
    table: "order-tracking-shipments",
    name: OrderTrackingShipmentsSystemView.ALL,
    filters: [],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: orderTrackingShipmentSortDefault,
    position: 0,
    isNotEditable: true,
  },
  [OrderTrackingShipmentsSystemView.LABEL_CREATED]: {
    table: "order-tracking-shipments",
    name: OrderTrackingShipmentsSystemView.LABEL_CREATED,
    filters: [getDeliveryStatusFilter(["pre_transit"])],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: orderTrackingShipmentSortDefault,
    position: 0,
  },
  [OrderTrackingShipmentsSystemView.SEVEN_DAYS_IN_TRANSIT]: {
    table: "order-tracking-shipments",
    name: OrderTrackingShipmentsSystemView.SEVEN_DAYS_IN_TRANSIT,
    filters: [
      getDeliveryStatusFilter([
        "in_transit",
        "out_for_delivery",
        "stalled_in_transit",
        "delayed",
      ]),
      getFirstInTransitBeforeRelativeDaysFilter(7),
    ],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: orderTrackingShipmentSortDefault,
    position: 0,
  },
  [OrderTrackingShipmentsSystemView.IN_TRANSIT]: {
    table: "order-tracking-shipments",
    name: OrderTrackingShipmentsSystemView.IN_TRANSIT,
    filters: [getDeliveryStatusFilter(["in_transit"])],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: orderTrackingShipmentSortDefault,
    position: 0,
  },
  [OrderTrackingShipmentsSystemView.OUT_FOR_DELIVERY]: {
    table: "order-tracking-shipments",
    name: OrderTrackingShipmentsSystemView.OUT_FOR_DELIVERY,
    filters: [getDeliveryStatusFilter(["out_for_delivery"])],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: orderTrackingShipmentSortDefault,
    position: 0,
  },
  [OrderTrackingShipmentsSystemView.DELIVERED]: {
    table: "order-tracking-shipments",
    name: OrderTrackingShipmentsSystemView.DELIVERED,
    filters: [getDeliveryStatusFilter(["delivered"])],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: orderTrackingShipmentSortDefault,
    position: 0,
  },
  [OrderTrackingShipmentsSystemView.EXCEPTIONS]: {
    table: "order-tracking-shipments",
    name: OrderTrackingShipmentsSystemView.EXCEPTIONS,
    filters: [getDeliveryStatusFilter(["delivery_failure", "delayed"])],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: orderTrackingShipmentSortDefault,
    position: 0,
  },
  [OrderTrackingShipmentsSystemView.PROBLEM_ORDERS]: {
    table: "order-tracking-shipments",
    name: OrderTrackingShipmentsSystemView.PROBLEM_ORDERS,
    filters: [
      getDeliveryStatusFilter([
        "stalled_in_transit",
        "return_to_sender",
        "delivery_failure",
        "shipment_cancelled",
        "shipment_error",
      ]),
    ],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: orderTrackingShipmentSortDefault,
    position: 0,
  },
};

/**
 * Resolves a view slug to its static system-view definition, or `undefined`
 * for custom views. Lets callers (e.g. the shipments deep link from problem
 * orders analytics) fall back to the static filter when a team's persisted
 * views haven't been seeded/backfilled yet.
 */
export function getStaticOrderTrackingShipmentView(
  viewName: string | undefined,
): viewTypes["order-tracking-shipments"] | undefined {
  const systemView = Object.values(OrderTrackingShipmentsSystemView).find(
    (systemViewName) => systemViewName === viewName,
  );
  return systemView ? StaticSystemViews[systemView] : undefined;
}

export function friendlyOrderTrackingShipmentViewName(
  viewName: string,
): string {
  switch (viewName) {
    case OrderTrackingShipmentsSystemView.ALL:
      return "All";
    case OrderTrackingShipmentsSystemView.LABEL_CREATED:
      return "Label Created";
    case OrderTrackingShipmentsSystemView.SEVEN_DAYS_IN_TRANSIT:
      return "7 Days in Transit";
    case OrderTrackingShipmentsSystemView.IN_TRANSIT:
      return "In Transit";
    case OrderTrackingShipmentsSystemView.OUT_FOR_DELIVERY:
      return "Out for Delivery";
    case OrderTrackingShipmentsSystemView.DELIVERED:
      return "Delivered";
    case OrderTrackingShipmentsSystemView.EXCEPTIONS:
      return "Exceptions";
    case OrderTrackingShipmentsSystemView.PROBLEM_ORDERS:
      return "Problem Orders";
    default:
      return viewName;
  }
}
